// THE STRUCTURED ISSUE an ask is ABOUT, and the ONE rule that decides which of
// the plan's pieces may carry it.
//
// WHY A FIELD AND NOT PROSE. An ask's text may MENTION an issue — "do what
// #42 asked for" — and that mention is not a link: it names no owner, no
// repository and no number the platform can check. A closing keyword is a
// one-way door: `Closes #42` closes an issue in the PR's OWN repository the
// moment that PR merges, and nothing downstream notices a wrong one. So the
// number that reaches a PR body comes from a field a CALLER stated, never from
// text a model read. {@link RawTaskIssueSchema} is that field: `{repo, number}`.
//
// THE REPOSITORY IS PART OF THE FIELD, and not decoration. A bare number is
// meaningless off a repository — `#42` is a different issue in every repo the
// account watches — and the number alone is what a closing keyword renders.
// Carrying the repository beside it is what lets the door REFUSE an ask whose
// issue lives in a repository the ask does not name
// (`issue_repo_not_in_ask`), before a row is written: a number the ask's own
// repos cannot account for is a number that would close something else.
//
// THE RULE, in one place, because two lanes need it and one of them writes the
// audit row that says why it did nothing: the TRIAGE lane, which cuts the ask
// into pieces and stamps `envelope.issue_number` on the ones that may carry it,
// and the RUNNER, whose PR body renders the closing keyword off that envelope.
// Two implementations of "which piece closes the issue" would disagree on the
// one question that closes an issue on merge.
//
// WHAT THE RULE DECIDES, for one ask and one plan:
//
//   - no issue named          → NOTHING is linked (`none`). An ask that merely
//     mentions an issue in prose links nothing, and says nothing about why:
//     there was never a link to make.
//   - exactly ONE piece in the issue's repository that opens a pull request
//     → that piece carries the number, and so do the NON-pull-request pieces
//     in that same repository (`linked`). The one PR is what closes the issue;
//     the report beside it narrates on the issue (`envelope.issue_number` is
//     the narration lane's source) and opens no PR, so it can never close
//     anything by accident.
//   - TWO OR MORE pull-request pieces in the issue's repository → NOTHING
//     carries the number (`skipped`, `multiple_pr_tasks`). Which of them would
//     close the issue is not decidable here, and a wrong `Closes #n` closes a
//     real issue on merge: the ask's own words are not enough to choose, so
//     the lane records why it linked nothing instead of guessing.
//   - NO pull-request piece in the issue's repository → NOTHING carries the
//     number (`skipped`, `no_pr_task`). No PR means no closing keyword, so
//     linking it would promise a closure that no piece can deliver.
//
// A piece in ANOTHER repository never carries the number, in every case: the
// number belongs to one repository's issue, and `Closes #n` in a pull request
// against a different repository would close THAT repository's `#n`.

import { z } from 'zod';
import { RepoFullNameSchema } from '../../value-objects/task-envelope';
import { OUTCOME_KIND_OPENS_PR, type OutcomeKind } from '../task/outcome';

/**
 * The GitHub issue an ask is ABOUT — `{repo, number}`.
 *
 * `repo` is the canonical `owner/name` ({@link RepoFullNameSchema}, the ONE
 * declaration of a full name in this repo) and `number` is a positive integer,
 * the same shape {@link TaskEnvelopeSchema}'s own `issue_number` carries: the
 * number that reaches a PR body must be one GitHub can close, and `0` or a
 * negative one is not an issue at all.
 *
 * THE ISSUE IS NOT AN `originRef`. `origin_ref` says which DELIVERY filed the
 * ask (`gh_issue:owner/repo#42`); this says which issue the ask is about, and
 * a person may file an ask about an issue through a door whose delivery names
 * nothing at all.
 */
export const RawTaskIssueSchema = z.object({
  repo: RepoFullNameSchema,
  number: z.number().int().positive(),
});
export type RawTaskIssue = z.infer<typeof RawTaskIssueSchema>;

/**
 * One piece of the plan the triage lane cut — the smallest thing this rule
 * decides about.
 *
 * `repo` is the piece's OWN repository (one piece, one repository: a box
 * checks out one tree), `outcomeKind` is what the piece DELIVERS, and `id` is
 * the plan-local id the lane knows the piece by — the id the lane stamps the
 * number onto.
 */
export type RawTaskIssuePiece = {
  readonly id: string;
  /** The piece's own repository, canonical `owner/name`. */
  readonly repo: string;
  readonly outcomeKind: OutcomeKind;
};

/**
 * WHY a link was NOT made. A closed set, because the lane writes it into an
 * audit row a human reads later: "the ask named an issue and nothing closed
 * it" is two different failures and they are told apart here.
 *
 * - `multiple_pr_tasks` — the plan opened more than one pull request in the
 *   issue's repository, and WHICH of them closes the issue is not decidable
 *   from the plan.
 * - `no_pr_task` — the plan opened no pull request in the issue's repository,
 *   so no piece of it can close the issue.
 */
export const RawTaskIssueLinkSkipReasonSchema = z.enum(['multiple_pr_tasks', 'no_pr_task']);
export type RawTaskIssueLinkSkipReason = z.infer<typeof RawTaskIssueLinkSkipReasonSchema>;

/**
 * WHAT THE LINK RULE DECIDED — a discriminated answer, never a nullable one:
 * "nothing was linked" and "the link was skipped, and here is why" are two
 * different facts, and a caller that collapses them writes an audit row that
 * says the wrong thing.
 *
 * - `none` — the ask named no issue. Nothing to link, and nothing to explain.
 * - `linked` — `pieceIds` are the pieces that carry `envelope.issue_number`
 *   (the ONE pull-request piece in the issue's repository, plus the
 *   non-pull-request pieces beside it), and `issueNumber` is the number they
 *   carry.
 * - `skipped` — NO piece carries the number, and `reason` says why.
 */
export type RawTaskIssueLink =
  | { kind: 'none' }
  | { kind: 'linked'; issueNumber: number; pieceIds: readonly string[] }
  | { kind: 'skipped'; reason: RawTaskIssueLinkSkipReason };

/**
 * THE LINK RULE — pure, and the one implementation of it.
 *
 * Given the ask's structured issue and the plan's pieces, it answers which
 * pieces carry `envelope.issue_number` and whether the link was skipped. The
 * module header states the four cases and why each one is what it is; this
 * function is them, in code.
 *
 * IT READS NOTHING AND WRITES NOTHING: the pieces are the lane's own plan,
 * already decided, and the issue is the ask's own row. A rule that needed a
 * read would belong to the lane, and the two lanes that read this rule
 * (triage and the runner's body) would then own two rules.
 *
 * THE REPOSITORY COMPARISON IS EXACT. Both sides are canonical `owner/name`:
 * the piece's repository is the one the plan was cut against, and the issue's
 * is the one the door admitted into the ask's own repository list. A
 * case-folded comparison would let `Owner/Repo` link to `owner/repo`'s issue
 * and stamp a number onto a repository the ask never named.
 */
export function rawTaskIssueLink(
  issue: RawTaskIssue | null | undefined,
  pieces: readonly RawTaskIssuePiece[],
): RawTaskIssueLink {
  if (issue === null || issue === undefined) return { kind: 'none' };
  const inRepo = pieces.filter((piece) => piece.repo === issue.repo);
  // `OUTCOME_KIND_OPENS_PR` is the totality table over the outcome kinds, so a
  // kind added later is decided there and not by a second `=== 'pr'` test here.
  const opening = inRepo.filter((piece) => OUTCOME_KIND_OPENS_PR[piece.outcomeKind]);
  if (opening.length > 1) return { kind: 'skipped', reason: 'multiple_pr_tasks' };
  if (opening.length === 0) return { kind: 'skipped', reason: 'no_pr_task' };
  return {
    kind: 'linked',
    issueNumber: issue.number,
    pieceIds: inRepo.map((piece) => piece.id),
  };
}
